import { useEffect, useMemo, useState } from 'react';
import type { UserListItem, UserPractice } from '../../../services/users';

export interface UserDrawerFormState {
    fullname: string;
    email: string;
    practices: UserPractice[];
    /** Create-only: defaults on, per the design source. Ignored on edit. */
    sendPasswordSetupEmail: boolean;
}

function buildInitialState(user: UserListItem | null): UserDrawerFormState {
    if (!user) {
        return { fullname: '', email: '', practices: [], sendPasswordSetupEmail: true };
    }
    return {
        fullname: user.fullname,
        email: user.email,
        practices: user.practices,
        sendPasswordSetupEmail: true
    };
}

function serialize(state: UserDrawerFormState): string {
    return JSON.stringify({
        fullname: state.fullname.trim(),
        email: state.email.trim().toLowerCase(),
        practiceIds: state.practices.map((practice) => practice.id).sort(),
        sendPasswordSetupEmail: state.sendPasswordSetupEmail
    });
}

/**
 * Owns the drawer's local form state for both create and edit, and reports
 * `isDirty` (state serialized against the snapshot taken when the drawer
 * opened) so the drawer can guard closes/cancels with a discard-changes
 * confirm instead of silently dropping edits.
 */
export function useUserDrawerForm(user: UserListItem | null) {
    const initial = useMemo(() => buildInitialState(user), [user]);
    const [state, setState] = useState<UserDrawerFormState>(initial);

    useEffect(() => {
        setState(initial);
    }, [initial]);

    const isDirty = serialize(state) !== serialize(initial);

    return { state, setState, isDirty };
}
